import { useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { key, STALE_TIME } from '@shared/api/query-keys';

import { composeCategoryTree, type WireCategoryRow } from '../_shared/tree';
import type { CategoryTreePayload } from '../_shared/types';

/**
 * The genre tree, full depth.
 *
 * **There is no `admin/categories` read.** The owning service publishes the tree
 * once, at `GET /api/v1/categories`, and its administration family carries the
 * three writes and no list of its own — so the console reads the same route the
 * public navbar reads. That is correct rather than a workaround: a category has
 * no draft state, no owner scoping, and no operator-only field, so an
 * operator-scoped projection would return the identical rows behind a second
 * contract.
 *
 * The key is the one `@shared/api/query-keys` composes from the REST URL, so the
 * writes on this surface and the shelf dialog's parameter picker two routes away
 * invalidate the same tuple.
 */
export function categoriesKey() {
  return key('content', 'categories');
}

export function useCategoryTree() {
  return useQuery({
    queryKey: categoriesKey(),
    // The service answers `data` as a FLAT bare array of rows; the nesting every
    // walk on this surface performs is composed here, once, at the query edge.
    queryFn: async (): Promise<CategoryTreePayload> => ({
      items: composeCategoryTree(asRows<WireCategoryRow>(await api.get('content', '/categories'))),
    }),
    // A vocabulary changes about once a quarter. Every write on this surface
    // invalidates it explicitly, so the long window costs the operator nothing
    // and saves a refetch on every return to the tab.
    staleTime: STALE_TIME.vocabulary,
  });
}
